import { trackingUtils, type trackingTypes } from '@debijenkorf/dbk-common';

// Schema-agnostic dbk-common replacements for
// getGA4ProductModel, trackingUtils.itemEvent(), & trackingUtils.viewItemList()

export type GA4Item = trackingTypes.GA4EcommerceItem & {
  item_size?: string;
  item_color?: string;
  item_discount_signing?: string;
  item_variant_stock?: boolean;
};

export type GA4ProductItemInput = {
  id: string;
  name: string;
  brand?: string;
  variant?: string;
  locationId: string;
  price?: number;
  quantity?: number;
  categoryPath?: string[];
  itemListId?: string;
  itemListName?: string;
  index?: number;
  size?: string;
  color?: string;
  discount?: number;
  discountSigning?: string;
  variantStock?: boolean;
  isListEvent?: boolean;
};

export type MapListProductToGA4ItemArgs<Product> = {
  product: Product;
  locationId: string;
  index?: number;
  itemListId?: string;
  itemListName?: string;
  isListEvent?: boolean;
};

type ItemCategory = trackingTypes.GA4DynamicItemCategories & { item_category?: string };

const getCategoryData = (categoryPath: string[] = []) => {
  let counter = 1;

  return categoryPath.reduce((accumulator: ItemCategory, name) => {
    accumulator[`item_category${counter === 1 ? '' : counter}`] = name;
    counter += 1;
    return accumulator;
  }, {});
};

export const getGA4ProductModel = (input: GA4ProductItemInput): GA4Item => {
  const commonAttributes = {
    item_name: input.name,
    item_id: input.id,
    item_brand: input.brand,
    item_variant: input.variant,
    location_id: input.locationId,
    price: input.price,
    quantity: input.quantity,
    index: input.index,
    ...getCategoryData(input.categoryPath),
    ...(input.itemListId && { item_list_id: input.itemListId }),
    ...(input.itemListName && { item_list_name: input.itemListName }),
  };

  const itemAttributes = {
    affiliation: 'Bijenkorf Online',
    discount: input.discount,
    ...(input.size && { item_size: input.size }),
  };

  const itemListAttributes = {
    item_variant_stock: input.variantStock,
    ...(input.color && { item_color: input.color }),
    ...(input.discountSigning && { item_discount_signing: input.discountSigning }),
  };

  return {
    ...commonAttributes,
    ...(input.isListEvent ? itemListAttributes : itemAttributes),
  };
};

// viewItemEvent()
// use for view_item tracking event
export type GA4ViewItemEvent = {
  event: 'view_item';
  ecommerce_ga4: {
    currency: 'EUR';
    items: GA4Item[];
  };
};

export const viewItemEvent = (input: GA4ProductItemInput): GA4ViewItemEvent => ({
  event: 'view_item',
  ecommerce_ga4: {
    currency: 'EUR',
    items: [getGA4ProductModel(input)],
  },
});

// selectItemEvent()
// use for select_item tracking event
export type GA4SelectItemEvent = {
  event: 'select_item';
  item_list_name?: string;
  ecommerce_ga4: {
    currency: 'EUR';
    items: GA4Item[];
  };
};

export const selectItemEvent = (input: GA4ProductItemInput): GA4SelectItemEvent => ({
  event: 'select_item',
  ...(input.itemListName && { item_list_name: input.itemListName }),
  ecommerce_ga4: {
    currency: 'EUR',
    items: [getGA4ProductModel(input)],
  },
});

// viewItemList()
// use for view_item_list tracking event
export type GA4ViewItemListEvent = {
  event: 'view_item_list';
  unique_event_id: string;
  ecommerce_ga4: {
    currency: 'EUR';
    items: GA4Item[];
  };
};

export const viewItemListEvent = (inputs: GA4ProductItemInput[]): GA4ViewItemListEvent => ({
  event: 'view_item_list',
  unique_event_id: trackingUtils.generateRandomUUID(),
  ecommerce_ga4: {
    currency: 'EUR',
    items: inputs.map(input => getGA4ProductModel({ ...input, isListEvent: true })),
  },
});

// wishlistOrCartEvent()
// use for add_to_cart, remove_from_cart, add_to_wishlist, remove_from_wishlist tracking events
export type GA4WishlistOrCartEvent = {
  event: 'add_to_cart' | 'remove_from_cart' | 'add_to_wishlist' | 'remove_from_wishlist';
  ecommerce_ga4: {
    currency: 'EUR';
    items: GA4Item[];
    unique_event_id: string;
    value?: number;
  };
};

export const wishlistOrCartEvent = (
  event: GA4WishlistOrCartEvent['event'],
  input: GA4ProductItemInput,
): GA4WishlistOrCartEvent => {
  const item = getGA4ProductModel(input);

  return {
    event,
    ecommerce_ga4: {
      currency: 'EUR',
      items: [item],
      unique_event_id: trackingUtils.generateRandomUUID(),
      value: item.price && input.quantity ? input.quantity * item.price : undefined,
    },
  };
};
